<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import inboxesAPI from 'dashboard/api/inboxes';
import { standardDateTime, formatDuration } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { isAdmin } = useAdmin();

const logs = ref([]);
const isFetching = ref(true);

const durationInSeconds = log => {
  const end = log.reconnected_at ? new Date(log.reconnected_at) : new Date();
  const start = new Date(log.disconnected_at);
  return Math.max(0, Math.round((end - start) / 1000));
};

onMounted(async () => {
  // The endpoint is admin-only (InboxPolicy#connection_logs?) - skip the doomed request
  // for agents instead of letting it 403.
  if (!isAdmin.value) {
    isFetching.value = false;
    return;
  }

  try {
    const { data } = await inboxesAPI.getConnectionLogs(props.inbox.id);
    logs.value = data;
  } catch (error) {
    // Diagnostic-only widget - fail silently and just stay empty rather than
    // surface an alert for a section the agent didn't explicitly ask to load.
  } finally {
    isFetching.value = false;
  }
});
</script>

<template>
  <div v-if="!isFetching && logs.length" class="mx-6 mb-6 max-w-4xl">
    <div
      class="px-5 py-5 space-y-4 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2"
    >
      <div>
        <span class="text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.CONNECTION_HISTORY.TITLE') }}
        </span>
        <p class="mt-1 text-body-main text-n-slate-11">
          {{ t('INBOX_MGMT.CONNECTION_HISTORY.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex flex-col gap-2">
        <div
          v-for="log in logs"
          :key="log.id"
          class="flex items-center justify-between gap-3 p-3 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-col min-w-0">
            <span class="text-label text-n-slate-12">
              {{ standardDateTime(log.disconnected_at) }}
            </span>
            <span class="text-label-small text-n-slate-11">
              {{
                log.reconnected_at
                  ? t('INBOX_MGMT.CONNECTION_HISTORY.RECONNECTED_AT', {
                      date: standardDateTime(log.reconnected_at),
                    })
                  : t('INBOX_MGMT.CONNECTION_HISTORY.STILL_DISCONNECTED')
              }}
            </span>
          </div>
          <span
            class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2 flex-shrink-0"
            :class="log.reconnected_at ? 'text-n-slate-11' : 'text-n-ruby-11'"
          >
            {{
              log.reconnected_at
                ? formatDuration(durationInSeconds(log))
                : t('INBOX_MGMT.CONNECTION_HISTORY.ONGOING')
            }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
